// AM Advertising Home — comparison-range semantics (pinned per the brief). PURE.
//
// - previous:  the immediately-preceding window of equal length (the existing
//              reportingDateRange.previousRange — reused, not re-derived).
// - lastMonth: the same from/to dates ONE MONTH earlier, day-of-month CLAMPED
//              for short months (Mar 31 → Feb 28/29).
// - lastYear:  the same dates one year earlier (Feb 29 → Feb 28). Calendar-
//              exact, deliberately simple — day-of-week alignment is a known
//              refinement, noted not built.

import { previousRange, type DateRange } from '../reportingDateRange.js';
import type { ComparisonMode } from '../../types/advertisingHome.js';

export const COMPARISON_MODES: Array<{ value: ComparisonMode; label: string }> = [
  { value: 'previous', label: 'vs previous period' },
  { value: 'lastMonth', label: 'vs same period last month' },
  { value: 'lastYear', label: 'vs same period last year' },
];

function daysIn(year: number, month1: number): number {
  return new Date(Date.UTC(year, month1, 0)).getUTCDate();
}

/** Shift a YYYY-MM-DD by whole months, clamping the day into the target month. */
export function shiftMonthsClamped(date: string, months: number): string {
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7)); // 1-based
  const d = Number(date.slice(8, 10));
  // Normalise the target month across year boundaries.
  const totalMonths = y * 12 + (m - 1) + months;
  const ty = Math.floor(totalMonths / 12);
  const tm = (totalMonths % 12) + 1; // back to 1-based
  const day = Math.min(d, daysIn(ty, tm));
  return `${ty}-${String(tm).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Shift by whole years (a 12-month shift, so Feb 29 clamps to Feb 28). */
export function shiftYearsClamped(date: string, years: number): string {
  return shiftMonthsClamped(date, years * 12);
}

export function comparisonRange(range: DateRange, mode: ComparisonMode): DateRange {
  switch (mode) {
    case 'previous':
      return previousRange(range);
    case 'lastMonth':
      return { from: shiftMonthsClamped(range.from, -1), to: shiftMonthsClamped(range.to, -1) };
    case 'lastYear':
      return { from: shiftYearsClamped(range.from, -1), to: shiftYearsClamped(range.to, -1) };
  }
}
